export type ChatRoomType = "direct" | "group" | "project";

/** 기능 연동 방 용도 코드 — null/undefined = 일반 대화방. 새 연동 기능은 여기에 코드·메타만 추가하면 된다 */
export type ChatRoomPurpose = "customer_inquiry";
export const CHAT_ROOM_PURPOSE_META: Record<ChatRoomPurpose, { label: string; banner: string; pendingLabel: string }> = {
  customer_inquiry: {
    label: "📩 고객문의",
    banner:
      "고객 포털 문의 연동 방입니다. 문의 메시지의 [고객에게 답변하기]로 답하면 고객 화면에 답변으로 표시됩니다. 일반 메시지는 내부 논의용으로 고객에게 보이지 않습니다.",
    // pending_count 배지 라벨 — 미읽음과 달리 답변 전까지 남는다
    pendingLabel: "답변 대기",
  },
};

// file_bundle: '묶어서 보내기'로 전송된 첨부 N개짜리 메시지
// inquiry: IPEX 고객 포털 문의 — [답장]하면 고객 화면에 답변으로 전달된다
export type MessageType = "text" | "file" | "image" | "video" | "system" | "poll" | "file_bundle" | "inquiry" | "sticker";

export interface ChatRoom {
  id: number;
  name?: string;
  room_type: ChatRoomType;
  /** 기능 연동 방 용도 코드 (CHAT_ROOM_PURPOSE_META 키) — 없으면 일반 대화방 */
  purpose?: ChatRoomPurpose | string | null;
  /** purpose 방의 처리 대기 건수 (예: 답변 대기 문의 수) — 읽어도 답변 전까지 남는다 */
  pending_count?: number | null;
  project_id?: number;
  created_by: string;
  created_at: string;
  members?: ChatRoomMember[];
  last_message?: Message;
  unread_count?: number;
}

export interface ChatMemberUser {
  id: string;
  name: string;
  email: string;
  profile_image?: string;
}

export interface ChatRoomMember {
  id: number;
  room_id: number;
  user_id: string;
  last_read_at?: string;
  joined_at: string;
  user?: ChatMemberUser;
}

export interface Message {
  id: number;
  room_id: number;
  user_id: string;
  content: string;
  message_type: MessageType;
  reply_to_id?: number;
  is_pinned: boolean;
  is_deleted?: boolean;
  created_at: string;
  updated_at: string;
  reactions?: MessageReaction[];
  attachments?: MessageAttachment[];
  extra_data?: {
    poll_id?: number;
    poll_title?: string;
    poll_total_voters?: number;
    poll_has_voted?: boolean;
    poll_is_closed?: boolean;
    [key: string]: any;
  };
}

export interface MessageReaction {
  id: number;
  message_id: number;
  user_id: string;
  emoji: string;
  created_at: string;
}

export interface MessageAttachment {
  id: number;
  message_id: number;
  file_name: string;
  file_path: string;
  has_thumbnail?: boolean;
  file_size: number;
  file_type: string;
}

export interface RoomAttachment {
  id: number;
  message_id: number;
  file_name: string;
  file_path: string;
  has_thumbnail?: boolean;
  file_size: number;
  file_type: string;
  created_at: string;
  user_name?: string;
  user_id?: string;
}

export interface ChatRoomCreateRequest {
  name?: string;
  room_type?: ChatRoomType;
  member_ids: string[];
  project_id?: number;
}

export interface LinkPreview {
  ok: boolean;
  url: string;
  title?: string | null;
  description?: string | null;
  image?: string | null;
  site_name?: string | null;
}

export interface MessageCreateRequest {
  content: string;
  message_type?: MessageType;
  reply_to_id?: number;
}

// WebSocket message types
export type WebSocketFrameType =
  | "message"
  | "typing"
  | "read"
  | "online"
  | "online_list"
  | "notification"
  | "notification_count"
  | "pong"
  | "message_deleted"
  | "reaction_added"
  | "reaction_removed"
  | "poll_updated"
  | "poll_deleted"
  | "reservation_updated"
  | "desktop_presence"
  | "desktop_command"
  // 서버가 클라이언트 입력을 거부했을 때 (허용되지 않은 유형·길이 초과·다른 방 답장·멤버 아님 등)
  | "error";

export interface WebSocketMessage {
  type: WebSocketFrameType;
  payload: unknown;
  silent?: boolean;
}

/** 'error' 프레임 payload — 전송 중(pending) 메시지를 즉시 실패 처리하는 데 쓴다 */
export interface ChatErrorPayload {
  code: string;
  message: string;
  room_id?: number;
}

/** 'read' 프레임 payload. 서버는 읽은 본인의 다른 연결(다른 창)에도 보낸다 — 창 간 안읽음 동기화 */
export interface ReadReceiptPayload {
  room_id: number;
  user_id: string;
  last_read_message_id: number;
}

// 읽음 상태: Map<roomId, Map<userId, lastReadMessageId>>
export type ReadReceiptsMap = Map<number, Map<string, number>>;

export interface TypingPayload {
  room_id: number;
  user_id: string;
  is_typing: boolean;
}

export interface OnlinePayload {
  user_id: string;
  is_online: boolean;
}
